import { DEFAULT_PAGINATION } from '@/commons/constants';
import { FILE_NAME_TYPE } from '@/commons/constants/common';
import { UploaderService } from '@/services';
import { FileModel, PaginationType, RootModel, StoragesModel, UserType } from '@/types';
import { createModel } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { Dispatch } from '../store';

const initialState: StoragesModel = {
  ids: [],
  pagination: DEFAULT_PAGINATION as PaginationType,
  loading: false,
  criteria: {
    keyword: '',
    tags: [],
  },
};

export const storage = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: Dispatch) => ({
    async filterAsync(payload): Promise<void> {
      this.updateLoading(true);
      try {
        let readyPayload = payload;
        if (readyPayload && readyPayload.sort) {
          const clonedPayload = { ...readyPayload };
          const { sort } = clonedPayload;
          if (sort) {
            clonedPayload.sort = sort.map((i) => `${i.property},${i.direction}`).join(',');
          }
          readyPayload = clonedPayload;
        }

        const data: { content: FileModel[] } & PaginationType =
          await dispatch.storageEntities.load(readyPayload);
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, pageable, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to get storages');
      } finally {
        this.updateLoading(false);
      }
    },
    async reloadList(payload, state) {
      const { criteria, pagination } = state.storage;
      await this.filterAsync({ ...(pagination as object), ...(criteria as object) });
    },
    async uploadFiles(payload, state) {
      this.updateLoading(true);
      try {
        const { user } = state.app as { user: UserType };
        const files = payload;
        if (files && !!files.length) {
          return await UploaderService.uploadFiles(files, FILE_NAME_TYPE.STORAGE, user.id).then(
            (result) => {
              return result.filesList;
            }
          );
        }
        message.success('Upload successfully');
        return [];
      } catch (err) {
        console.error(err);
        message.error('Fail to upload');
        return [];
      } finally {
        this.updateLoading(false);
      }
    },
    async deleteAsync(payload, state) {
      const { id, name } = payload;
      this.updateLoading(true);
      try {
        await dispatch.storageEntities.delete(id);
        await this.reloadList();
        message.success(`Success to delete media: ${name}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to delete media. ${name} may be in use.`);
      } finally {
        this.updateLoading(false);
      }
    },
    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const res = await dispatch.storageEntities.save(payload);
        message.success('Save successfully');

        return res;
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});

export default storage;
